import React, { useCallback, useEffect, useRef, useState } from 'react';
import type { OrderDetails } from '../../services/orders.service';
import ordersService from '../../services/orders.service';
import { useToast } from '../../contexts/ToastContext';
import PermissionsContainer from '../PermissionsContainer';
import { computeHazDocsEligibility } from '../../utils/hazDocsEligibility';

interface SendHazDocsActionsProps {
  orderDetails: OrderDetails;
  /** When true, the button is disabled — used to defer the action while an
   * unrelated save is in flight. */
  busy?: boolean;
  /** Invoked after a successful send so the page can re-fetch and reflect the
   * new hazreqdatetime. */
  onRefresh?: () => void;
}

/**
 * Footer "Send Haz Docs" action. Mirrors v1 lib_editrecord.pl's
 * `$inlinehazdocbutton` block — emails the pro's HAZ-MAT PAPERWORK docs to the
 * CSR / dispatcher / haz@poha.com and stamps hazreqdatetime.
 *
 * Gated by the `send-haz-docs` PermissionsContainer (hidden by default), same
 * pattern as OrderReuseActions / RailBillingActions, and only rendered when the
 * pro is eligible (computeHazDocsEligibility). A confirm dialog guards the send.
 */
const SendHazDocsActions: React.FC<SendHazDocsActionsProps> = ({
  orderDetails,
  busy = false,
  onRefresh,
}) => {
  const { showToast } = useToast();
  const eligible = computeHazDocsEligibility(orderDetails);

  const [confirmOpen, setConfirmOpen] = useState(false);
  const [sending, setSending] = useState(false);

  const dialogRef = useRef<HTMLDialogElement>(null);
  useEffect(() => {
    const dlg = dialogRef.current;
    if (!dlg) return;
    if (confirmOpen && !dlg.open) dlg.showModal();
    else if (!confirmOpen && dlg.open) dlg.close();
  }, [confirmOpen]);

  const errMessage = (err: unknown): string => {
    const e = err as { response?: { data?: { detail?: unknown } }; message?: string };
    const detail = e?.response?.data?.detail;
    if (typeof detail === 'string') return detail;
    return e?.message || 'Request failed';
  };

  const handleSend = useCallback(async () => {
    setSending(true);
    try {
      const res = await ordersService.sendHazDocs(orderDetails.pronumber);
      showToast({ type: res.success ? 'success' : 'info', message: res.message });
      setConfirmOpen(false);
      onRefresh?.();
    } catch (err) {
      showToast({ type: 'error', message: `Send haz docs failed: ${errMessage(err)}` });
    } finally {
      setSending(false);
    }
  }, [orderDetails.pronumber, onRefresh, showToast]);

  if (!eligible) return null;

  return (
    <PermissionsContainer
      componentId="send-haz-docs"
      componentName="Send Haz Docs"
      defaultHidden
    >
      <button
        type="button"
        onClick={() => setConfirmOpen(true)}
        disabled={busy || sending}
        className="px-4 py-1.5 bg-orange-600 hover:bg-orange-700 disabled:bg-gray-300 disabled:cursor-not-allowed text-white text-sm rounded-md transition-colors"
      >
        {sending ? 'Sending…' : 'Send Haz Docs'}
      </button>

      <dialog
        ref={dialogRef}
        onCancel={(e) => {
          e.preventDefault();
          if (!sending) setConfirmOpen(false);
        }}
        className="bg-transparent p-0 backdrop:bg-gray-900/50 backdrop:backdrop-blur-sm rounded-lg"
      >
        <div className="bg-panel rounded-lg shadow-xl p-6 max-w-sm w-full mx-4">
          <h3 className="text-base font-semibold text-heading mb-2">
            Send Haz Docs
          </h3>
          <p className="text-sm text-gray-600 dark:text-gray-400 mb-6">
            This will email the HAZ-MAT PAPERWORK for PRO {orderDetails.pronumber} to
            the CSR, dispatcher, and haz@poha.com, and mark haz docs as requested.
            Continue?
          </p>
          <div className="flex justify-end gap-3">
            <button
              type="button"
              onClick={() => setConfirmOpen(false)}
              disabled={sending}
              className="px-4 py-2 text-sm font-medium text-label bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-md hover:bg-gray-50 dark:hover:bg-gray-600 disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={handleSend}
              disabled={sending}
              className="px-4 py-2 text-sm font-medium text-white bg-orange-600 hover:bg-orange-700 disabled:bg-gray-300 rounded-md"
            >
              {sending ? 'Sending…' : 'Send Haz Docs'}
            </button>
          </div>
        </div>
      </dialog>
    </PermissionsContainer>
  );
};

export default SendHazDocsActions;
